import React, { useContext, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import ExpandNavbarContext from 'Shared/contexts/ExpandNavbarContext';
import { getAbbreviation } from 'Shared/helpers';
import UnreadNotificationsBubble from 'Dashboard/components/UnreadNotificationsBubble/UnreadNotificationsBubble';
import WpBadge from 'Wpds/components/badge/WpBadge';
import WpAvatarGroup from 'Wpds/components/avatar/WpAvatarGroup';
import ContextMenuWrapper from '../ContextMenuWrapper/ContextMenuWrapper';
import { WpTooltip, WpTooltipTrigger, WpTooltipContent } from 'Wpds/components/tooltip';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import './GroupItem.scss';
type Props = {
  workspaceId: string;
  name: string;
  users: Array<{
    id: number;
    fullName: string;
    email: string;
    avatar: {
      color: string;
      initials: string;
      profilePictureUrl: string;
    };
  }>;
  showNewBadge: boolean;
  workspaceNotificationsCount: number;
  disabled?: boolean;
  path: string;
  onCloseNavbar: Function;
  starred: boolean;
};

function GroupItem({
  name,
  workspaceId,
  users,
  path,
  onCloseNavbar,
  showNewBadge,
  workspaceNotificationsCount,
  starred,
  disabled = false,
}: Props) {
  const isExpanded = useContext(ExpandNavbarContext);
  const isCurrentWorkspace = useSelector(state => state.workspace.id === workspaceId);

  const abbreviationName = getAbbreviation(name, 2);
  const { tr } = useTranslation();

  const handleClick = useCallback(
    event => {
      if (disabled) event.preventDefault();
      else {
        onCloseNavbar();
      }
    },
    [disabled, onCloseNavbar]
  );

  return (
    <ContextMenuWrapper workspaceId={workspaceId} starred={starred}>
      <NavLink
        to={path}
        activeClassName="active"
        className={cn('group-item nav-link d-flex align-items-center justify-content-between my-2', {
          'group-item--disabled': disabled,
        })}
        onClick={handleClick}
      >
        {isExpanded ? (
          <>
            <div className="group-item-name pr-2">{name}</div>
            <div className="d-flex align-items-center">
              {!showNewBadge && workspaceNotificationsCount && !isCurrentWorkspace ? (
                <UnreadNotificationsBubble count={workspaceNotificationsCount} classNames="mr-1" />
              ) : null}
              {showNewBadge && (
                <span className="mr-1">
                  <WpBadge color="green">{tr('perform.navigation.team_group_section.new_badge')}</WpBadge>
                </span>
              )}
              <span className="mr-2">
                <WpAvatarGroup users={users} size="sm" limit={2} stacked showAdditionalCount />
              </span>
            </div>
          </>
        ) : (
          <div className="group-item--expanded">
            <WpTooltip placement="right">
              <WpTooltipTrigger>
                <div
                  className={cn(
                    'group-item-name-expanded d-flex justify-content-center align-items-center border border-primary text-primary circle position-relative mx-auto',
                    {
                      'group-item-name-expanded--notify': workspaceNotificationsCount,
                    }
                  )}
                >
                  {abbreviationName}
                </div>
              </WpTooltipTrigger>
              <WpTooltipContent>{name}</WpTooltipContent>
            </WpTooltip>
          </div>
        )}
      </NavLink>
    </ContextMenuWrapper>
  );
}

export default GroupItem;
